import { getQuestionnaireRoute } from '@/app/components/quizletv2/quizlet-config';
import { useGetQuestionWithNavigation } from '@/app/services/questionnaireV2.service';
import { ROUTE_DASHBOARD } from '@/constants/routes';
import { getRoute } from '@/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuestionnaireContext } from '../context/QuestionnaireContext';

export function useQuestionnaireNavigation(
  resetField?: (fieldName: string) => void,
  isQuestionAnswered?: (questionSlug: string) => boolean,
) {
  const { state, dispatch } = useQuestionnaireContext();
  const navigate = useNavigate();
  const { questionSlug } = useParams();
  const questionnaireWrapperRef = useRef<HTMLDivElement>(null);

  // Simple loading state to prevent rapid navigation
  const [isNavigating, setIsNavigating] = useState(false);

  // Determine which route to use based on current path and questionnaire
  const questionRoute = getQuestionnaireRoute(state.questionnaire);

  // Get server-driven navigation context for the current question
  const currentQuestionSlug = questionSlug || state.questions[state.currentQuestionIndex]?.slug;

  // All questionnaires now use v2 API with navigation context
  const shouldFetchNavigationContext = !!(
    state.questionnaire?.id &&
    state.responseId &&
    currentQuestionSlug &&
    state.hasRestoredSession // Only fetch after session is restored
  );

  const {
    data: navigationContext,
    error: navigationError,
    isLoading: isLoadingNavigationContext,
    isFetching: isFetchingNavigationContext,
    isPending: isPendingNavigationContext,
  } = useGetQuestionWithNavigation(
    state.questionnaire?.id || '',
    state.responseId || '',
    currentQuestionSlug || '',
    shouldFetchNavigationContext,
  );

  // Pure backend-driven navigation - always trust the server
  const previousQuestionSlug = navigationContext?.previousQuestionSlug;

  // Simple loading state - block during navigation or data fetching
  const isNavigationBusy =
    isNavigating ||
    isLoadingNavigationContext ||
    isFetchingNavigationContext ||
    isPendingNavigationContext;

  // Clear navigation state when URL changes
  useEffect(() => {
    if (questionSlug) {
      setIsNavigating(false);
    }
  }, [questionSlug]);

  // Handle all navigation and URL synchronization
  useEffect(() => {
    if (!state.questions.length) return;

    // If no questionSlug in URL, navigate to first question
    if (!questionSlug) {
      const firstQuestion = state.questions[0];
      navigate(getRoute(questionRoute, { questionSlug: firstQuestion.slug }), { replace: true });
      return;
    }

    const questionIndex = state.questions.findIndex((q) => q.slug === questionSlug);
    
    if (questionIndex === -1) {
      // Question not found - redirect to first question
      const firstQuestion = state.questions[0];
      navigate(getRoute(questionRoute, { questionSlug: firstQuestion.slug }), { replace: true });
      return;
    }

    // Update current question index if needed
    if (questionIndex !== state.currentQuestionIndex) {
      if (state.isHandlerNavigation) {
        // Navigation from our handlers - clear the flag
        dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: false });
      } else {
        // Browser navigation - reset form field for previous question
        const currentQuestion = state.questions[state.currentQuestionIndex];
        if (currentQuestion && resetField) {
          resetField(currentQuestion.slug);
        }
      }
      dispatch({ type: 'SET_CURRENT_QUESTION_INDEX', payload: questionIndex });
    }
  }, [state.questions, questionSlug, state.currentQuestionIndex, state.isHandlerNavigation, navigate, dispatch, questionRoute, resetField]);

  const navigateToQuestion = useCallback(
    (questionSlug: string, questionIndex: number) => {
      if (isNavigationBusy) return;
      
      setIsNavigating(true);
      dispatch({ type: 'SET_CURRENT_QUESTION_INDEX', payload: questionIndex });
      dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: true });
      navigate(getRoute(questionRoute, { questionSlug }));
    },
    [isNavigationBusy, dispatch, navigate, questionRoute],
  );

  const handleNext = useCallback(() => {
    const nextIndex = state.currentQuestionIndex + 1;
    const nextQuestion = state.questions[nextIndex];

    if (nextQuestion) {
      navigateToQuestion(nextQuestion.slug, nextIndex);
    }
  }, [state.currentQuestionIndex, state.questions, navigateToQuestion]);

  const handlePrevious = useCallback(() => {
    if (isNavigationBusy || !previousQuestionSlug) return;

    setIsNavigating(true);
    dispatch({ type: 'SET_NAVIGATION_DIRECTION', payload: 'backward' });
    dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: true });
    navigate(getRoute(questionRoute, { questionSlug: previousQuestionSlug }));
  }, [isNavigationBusy, previousQuestionSlug, dispatch, navigate, questionRoute]);

  const handleBack = useCallback(() => {
    if (isNavigationBusy) return;

    if (previousQuestionSlug) {
      handlePrevious();
    } else {
      setIsNavigating(true);
      dispatch({ type: 'SET_NAVIGATION_DIRECTION', payload: 'backward' });
      dispatch({ type: 'SET_HANDLER_NAVIGATION', payload: true });
      navigate(ROUTE_DASHBOARD);
    }
  }, [isNavigationBusy, previousQuestionSlug, handlePrevious, dispatch, navigate]);

  // Simple navigation state - purely backend-driven
  const canGoPrevious = previousQuestionSlug !== null;

  return {
    // Original interface
    currentQuestionIndex: state.currentQuestionIndex,
    canGoPrevious,
    isLastQuestion: state.currentQuestionIndex === state.questions.length - 1,
    questionnaireWrapperRef,
    handleNext,
    handlePrevious,
    handleBack,
    navigateToQuestion,

    // Enhanced navigation features
    previousQuestionSlug,

    // Navigation state
    navigationContext,
    isNavigationBusy,
  };
}
